/**
 * ============================================
 * MULTI-ORG MONITOR (LIVE)
 * ============================================
 *
 * Real multi-org dashboard backed by the failover engine API
 * (/api/v1/failover/*). No mocks. Polls every ~3s.
 *
 * Layout:
 *   - Common stats row (Total Players / Level Uploads / Avg Session)
 *   - Failover controls bar (admin only): Pause/Resume + Status pills
 *   - Two stacked org panels (APPY_DEV / APPY_ORG) each with Dev/Stage/Live tabs
 *   - Failover priority chain card (admin only)
 *   - Header-strip with Failover History dropdown (admin only) — shows
 *     lifecycle events colour-coded + audit log tab
 *
 * Admin gating reuses the existing pattern: user.groups.includes('admin').
 * Pause/Resume/Force POST the real API with the current user's email.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import {
  Code2, FlaskConical, Rocket, Building2,
  ShieldAlert, History, ArrowRight, CheckCircle2, AlertTriangle, Pause, Play,
  Activity, ScrollText, Shield,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { statusColors, type ServiceHealthStatus, BACKEND_URL } from '@/config';
import { useAuth } from '@/context/AuthContext';
import { getEffectiveRole } from '@/utils/roles';

// ---- Types --------------------------------------------------------------

type FailoverStatus = 'failed' | 'started' | 'in_progress' | 'done' | 'exhausted';

interface FailoverEvent {
  id: string;
  timestamp: string;
  reason: string;
  status?: FailoverStatus;
  fromOrg: string; fromEnv: string;
  toOrg: string;   toEnv: string;
  outcome: 'success' | 'failed' | 'detected';
  scaledAsg?: { org: string; env: string };
}

interface AuditEntry {
  id: string;
  timestamp: string;
  username: string;
  action: string;
  details: string;
}

interface FailoverStateResponse {
  enabled: boolean;
  active: { org: string; env: string };
  paused: boolean;
  forced: { org: string; env: string } | null;
  asg_desired: Record<string, Record<string, number | null>>;
  fault_state: Record<string, Record<string, Record<string, ServiceHealthStatus>>>;
  consecutive_failures: Record<string, Record<string, number>>;
  healthy_streak_seconds: Record<string, Record<string, number>>;
  tick_count: number;
  last_tick: string | null;
  remote_config_host: string;
  priority_chain: { org: string; env: string }[];
  orgs: Record<string, { display_name: string; aws_account_id: string; envs: string[] }>;
}

// Per-action colour for the audit log dropdown. Keys match alert_manager
// 'failover.*' action names recorded by services/failover.py.
const ACTION_STYLE: Record<string, { bg: string; text: string; border: string }> = {
  'failover.pause':       { bg: 'bg-yellow-500/15', text: 'text-yellow-300', border: 'border-yellow-500/40' },
  'failover.resume':      { bg: 'bg-green-500/15',  text: 'text-green-300',  border: 'border-green-500/40' },
  'failover.force':       { bg: 'bg-blue-500/15',   text: 'text-blue-300',   border: 'border-blue-500/40' },
  'failover.unforce':     { bg: 'bg-slate-500/15',  text: 'text-slate-300',  border: 'border-slate-500/40' },
  'failover.failed':      { bg: 'bg-red-500/15',    text: 'text-red-300',    border: 'border-red-500/40' },
  'failover.started':     { bg: 'bg-yellow-500/15', text: 'text-yellow-300', border: 'border-yellow-500/40' },
  'failover.in_progress': { bg: 'bg-blue-500/15',   text: 'text-blue-300',   border: 'border-blue-500/40' },
  'failover.done':        { bg: 'bg-green-500/15',  text: 'text-green-300',  border: 'border-green-500/40' },
  'failover.exhausted':   { bg: 'bg-red-700/25',    text: 'text-red-200',    border: 'border-red-700/60' },
  'failover.executed':    { bg: 'bg-purple-500/15', text: 'text-purple-300', border: 'border-purple-500/40' },
};

const STATUS_STYLE: Record<FailoverStatus, { bg: string; text: string; border: string; label: string }> = {
  failed:      { bg: 'bg-red-500/15',    text: 'text-red-300',    border: 'border-red-500/40',    label: 'FAILED' },
  started:     { bg: 'bg-yellow-500/15', text: 'text-yellow-300', border: 'border-yellow-500/40', label: 'STARTED' },
  in_progress: { bg: 'bg-blue-500/15',   text: 'text-blue-300',   border: 'border-blue-500/40',   label: 'IN PROGRESS' },
  done:        { bg: 'bg-green-500/15',  text: 'text-green-300',  border: 'border-green-500/40',  label: 'DONE' },
  exhausted:   { bg: 'bg-red-700/25',    text: 'text-red-200',    border: 'border-red-700/60',    label: 'EXHAUSTED' },
};

const ENV_ICONS: Record<string, React.ReactNode> = {
  dev: <Code2 className="h-4 w-4 sm:h-5 sm:w-5" />,
  stage: <FlaskConical className="h-4 w-4 sm:h-5 sm:w-5" />,
  live: <Rocket className="h-4 w-4 sm:h-5 sm:w-5" />,
};

// ---- Helpers ------------------------------------------------------------

function fmtTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
}

function envOverallStatus(services: Record<string, ServiceHealthStatus>): ServiceHealthStatus {
  const counted = Object.values(services).filter(s => s !== 'unknown' && s !== 'disabled');
  if (counted.some(s => s === 'critical')) return 'critical';
  if (counted.some(s => s === 'warning')) return 'warning';
  if (counted.length > 0 && counted.every(s => s === 'healthy')) return 'healthy';
  return 'unknown';
}

async function apiGet<T>(path: string): Promise<T> {
  const r = await fetch(`${BACKEND_URL}${path}`, { cache: 'no-store' });
  if (!r.ok) throw new Error(`${path} -> ${r.status}`);
  return r.json();
}

async function apiPost<T>(path: string, body: object): Promise<T> {
  const r = await fetch(`${BACKEND_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!r.ok) throw new Error(`${path} -> ${r.status}`);
  return r.json();
}

// ---- Sub-components -----------------------------------------------------

function CommonStatsRow() {
  // TODO: when the backend exposes /api/v1/common-stats (Total Players, Level
  // Uploads, Avg Session) wired across both orgs (DB is shared so values are
  // the same), fetch and render here. For now, hidden — the per-env panels
  // show the same data already.
  return null;
}

function FailoverControls({
  paused, forced, onTogglePause, onUnforce, busy,
}: {
  paused: boolean;
  forced: { org: string; env: string } | null;
  onTogglePause: () => void;
  onUnforce: () => void;
  busy: boolean;
}) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-xl border border-border bg-card/50 px-3 sm:px-4 py-3">
      <div className="flex items-start gap-2 sm:items-center">
        <ShieldAlert className={`h-5 w-5 mt-0.5 sm:mt-0 ${paused ? 'text-yellow-500' : 'text-green-500'} flex-shrink-0`} />
        <div>
          <p className="text-sm font-semibold flex items-center gap-2 flex-wrap">
            Auto-failover {paused ? 'PAUSED' : 'ACTIVE'}
            {forced && (
              <span className="inline-flex items-center gap-1 rounded-full bg-blue-500/15 text-blue-400 border border-blue-500/30 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
                Forced: {forced.org}/{forced.env}
              </span>
            )}
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
              <Shield className="h-3 w-3" /> Admin only
            </span>
          </p>
          <p className="text-xs text-muted-foreground">
            {paused
              ? 'Overseer will alert but will NOT modify remote-config or scale ASGs.'
              : '3 consecutive critical cycles on Nakama / ALB / Lambda will trigger a switchover.'}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        {forced && (
          <button
            onClick={onUnforce}
            disabled={busy}
            className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium border border-blue-500/30 bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 disabled:opacity-50"
          >
            Release pin
          </button>
        )}
        <button
          onClick={onTogglePause}
          disabled={busy}
          className={`inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors flex-shrink-0 disabled:opacity-50 ${
            paused
              ? 'bg-green-500/10 text-green-600 dark:text-green-400 hover:bg-green-500/20 border border-green-500/30'
              : 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 hover:bg-yellow-500/20 border border-yellow-500/30'
          }`}
        >
          {paused ? <><Play className="h-4 w-4" /> Resume</> : <><Pause className="h-4 w-4" /> Pause</>}
        </button>
      </div>
    </div>
  );
}

function ServicePill({ name, status }: { name: string; status: ServiceHealthStatus }) {
  const colour = statusColors[status] || statusColors.unknown;
  return (
    <div
      className="group relative inline-flex items-center gap-2 px-2.5 py-1 sm:px-3 sm:py-1.5 rounded-full bg-muted/40 border border-muted/60"
      title={`${name}: ${status}`}
    >
      <span
        className={`h-2 w-2 rounded-full flex-shrink-0 ${
          status === 'warning' || status === 'critical' ? 'animate-pulse' : ''
        }`}
        style={{ backgroundColor: colour }}
      />
      <span className="text-[11px] sm:text-xs font-medium">{name}</span>
    </div>
  );
}

interface EnvViewData {
  envId: string;
  services: Record<string, ServiceHealthStatus>;
  asgDesired: number | null;
  failStreak: number;
  healthyStreak: number;
}

function EnvDetailCard({ env, isActive }: { env: EnvViewData; isActive: boolean }) {
  const overall = envOverallStatus(env.services);
  const overallColour = statusColors[overall];
  const counted = Object.values(env.services).filter(s => s !== 'unknown' && s !== 'disabled');
  const healthy = counted.filter(s => s === 'healthy').length;

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className={`h-3 w-3 rounded-full ${overall === 'critical' || overall === 'warning' ? 'animate-pulse' : ''}`}
            style={{ backgroundColor: overallColour }}
          />
          <h3 className="text-base sm:text-lg font-semibold capitalize">{env.envId} Environment</h3>
          {isActive && (
            <span className="inline-flex items-center gap-1 rounded-full bg-green-500/15 text-green-600 dark:text-green-400 border border-green-500/30 px-2 py-0.5 text-[10px] sm:text-xs font-semibold uppercase tracking-wide">
              <Activity className="h-3 w-3" /> Active
            </span>
          )}
          {env.asgDesired != null && (
            <span className="text-[10px] sm:text-xs text-muted-foreground">ASG desired: {env.asgDesired}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: overallColour }} />
          <span className="text-xs sm:text-sm">{healthy}/{counted.length} healthy</span>
        </div>
      </div>

      {(env.failStreak > 0 || env.healthyStreak > 0) && (
        <div className="text-[11px] text-muted-foreground space-x-3">
          {env.failStreak > 0 && <span>fail streak: <span className="text-red-400 font-mono">{env.failStreak}</span></span>}
          {env.healthyStreak > 0 && <span>healthy: <span className="text-green-400 font-mono">{env.healthyStreak}s</span></span>}
        </div>
      )}

      <div className="flex flex-wrap gap-1.5 sm:gap-2">
        {Object.entries(env.services).map(([svc, status]) => (
          <ServicePill key={svc} name={svc} status={status} />
        ))}
      </div>
    </div>
  );
}

function OrgPanel({
  orgId, orgDisplayName, orgEnvs, activeEnv, isAdmin, busy, onForce,
}: {
  orgId: string;
  orgDisplayName: string;
  orgEnvs: EnvViewData[];
  activeEnv: string | null;
  isAdmin: boolean;
  busy: boolean;
  onForce: (env: string) => void;
}) {
  const initial = activeEnv ?? (orgEnvs.find(e => e.envId === 'live')?.envId ?? orgEnvs[0]?.envId ?? 'live');
  const [selected, setSelected] = useState<string>(initial);

  // Auto-jump to whichever env the engine just switched ACTIVE to (real
  // failover happened). Depend ONLY on activeEnv -- if we also depend on
  // `selected`, every user-click setSelected re-runs the effect and forces
  // the tab back to activeEnv ('live'), causing the flicker bug.
  useEffect(() => {
    if (activeEnv) setSelected(activeEnv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeEnv]);

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-col items-center gap-2 sm:gap-3">
          <div className="flex items-center gap-1.5 text-muted-foreground/70">
            <Building2 className="h-3.5 w-3.5" />
            <span className="text-[11px] sm:text-xs font-medium tracking-widest uppercase">{orgDisplayName}</span>
          </div>
          <Tabs value={selected} onValueChange={setSelected} className="w-full">
            <div className="flex justify-center">
              <TabsList className="bg-muted/50 p-1 sm:p-1.5 rounded-xl h-auto flex-wrap justify-center gap-1">
                {orgEnvs.map(env => {
                  const isActiveEnv = activeEnv === env.envId;
                  return (
                    <TabsTrigger
                      key={env.envId}
                      value={env.envId}
                      className="relative flex items-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-1.5 sm:py-2 text-xs sm:text-sm font-semibold data-[state=active]:bg-background rounded-lg"
                    >
                      {ENV_ICONS[env.envId] ?? <Code2 className="h-4 w-4" />}
                      <span className="capitalize">{env.envId}</span>
                      {isActiveEnv && (
                        <span
                          className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-green-500 ring-2 ring-background animate-pulse"
                          aria-label="Active endpoint"
                        />
                      )}
                    </TabsTrigger>
                  );
                })}
              </TabsList>
            </div>

            <CardContent className="px-0 pt-4 sm:pt-6">
              {orgEnvs.map(env => (
                <TabsContent key={env.envId} value={env.envId}>
                  <EnvDetailCard env={env} isActive={activeEnv === env.envId} />
                  {isAdmin && activeEnv !== env.envId && (
                    <div className="mt-4 flex justify-end">
                      <button
                        onClick={() => onForce(env.envId)}
                        disabled={busy}
                        className="text-xs px-2.5 py-1 rounded border border-blue-500/30 bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 disabled:opacity-50"
                      >
                        Pin {orgId}/{env.envId} as active
                      </button>
                    </div>
                  )}
                </TabsContent>
              ))}
            </CardContent>
          </Tabs>
        </div>
      </CardHeader>
    </Card>
  );
}

function FailoverHistoryButton({ events, audit }: { events: FailoverEvent[]; audit: AuditEntry[] }) {
  const [isOpen, setIsOpen] = useState(false);
  const [tab, setTab] = useState<'history' | 'audit'>('history');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const failedCount = events.filter(e => e.outcome === 'failed' || e.status === 'exhausted').length;
  const totalBadge = events.length + audit.length;

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(o => !o)}
        title="Failover history & audit log"
        className={`relative p-2 rounded-lg transition-colors ${
          failedCount > 0
            ? 'bg-red-500/20 hover:bg-red-500/30 text-red-400'
            : 'hover:bg-muted text-muted-foreground'
        }`}
      >
        <History className="h-5 w-5" />
        {totalBadge > 0 && (
          <span className={`absolute -top-1 -right-1 h-5 w-5 rounded-full text-xs font-bold flex items-center justify-center ${
            failedCount > 0 ? 'bg-red-500 text-white' : 'bg-blue-500 text-white'
          }`}>
            {totalBadge > 9 ? '9+' : totalBadge}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-[min(92vw,28rem)] bg-background border border-border rounded-lg shadow-lg z-50">
          <div className="border-b border-border">
            <div className="flex items-center justify-between px-4 pt-3 pb-2">
              <h3 className="font-semibold flex items-center gap-2">
                <Shield className="h-4 w-4 text-emerald-500" /> Failover
              </h3>
              <span className="text-[10px] uppercase tracking-wide text-emerald-600 dark:text-emerald-400">Admin only</span>
            </div>
            <div className="flex">
              <button
                onClick={() => setTab('history')}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium transition-colors border-b-2 ${
                  tab === 'history' ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <History className="h-3.5 w-3.5" /> History <span className="opacity-60">({events.length})</span>
              </button>
              <button
                onClick={() => setTab('audit')}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium transition-colors border-b-2 ${
                  tab === 'audit' ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <ScrollText className="h-3.5 w-3.5" /> Audit log <span className="opacity-60">({audit.length})</span>
              </button>
            </div>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {tab === 'history' ? (
              events.length === 0 ? (
                <div className="px-4 py-8 text-center text-muted-foreground">
                  <CheckCircle2 className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No failover events</p>
                </div>
              ) : (
                events.map(ev => {
                  const status = ev.status;
                  const style = status ? STATUS_STYLE[status] : null;
                  const isFailureStatus = status === 'failed' || status === 'exhausted';
                  return (
                    <div
                      key={ev.id}
                      className={`px-3 sm:px-4 py-3 border-b border-border last:border-0 ${
                        isFailureStatus ? 'bg-red-500/5' : ''
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        {isFailureStatus
                          ? <AlertTriangle className="h-4 w-4 mt-0.5 text-red-500 flex-shrink-0" />
                          : <CheckCircle2 className="h-4 w-4 mt-0.5 text-green-500 flex-shrink-0" />
                        }
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-1.5 text-xs">
                            {style && (
                              <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold tracking-wide border ${style.bg} ${style.text} ${style.border}`}>
                                {style.label}
                              </span>
                            )}
                            <span className="font-mono px-1.5 py-0.5 rounded bg-muted">
                              {ev.fromOrg.toUpperCase()} {ev.fromEnv}
                            </span>
                            <ArrowRight className="h-3 w-3 text-muted-foreground" />
                            <span className="font-mono px-1.5 py-0.5 rounded bg-muted">
                              {ev.toOrg.toUpperCase()} {ev.toEnv}
                            </span>
                          </div>
                          <p className="mt-1 text-xs text-muted-foreground line-clamp-2">{ev.reason}</p>
                          <p className="mt-1 text-[11px] text-muted-foreground">{fmtTime(ev.timestamp)}</p>
                        </div>
                      </div>
                    </div>
                  );
                })
              )
            ) : (
              audit.length === 0 ? (
                <div className="px-4 py-8 text-center text-muted-foreground">
                  <ScrollText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No audit entries yet</p>
                </div>
              ) : (
                audit.map(a => {
                  const actionStyle = ACTION_STYLE[a.action] ?? null;
                  return (
                    <div key={a.id} className="px-3 sm:px-4 py-3 border-b border-border last:border-0">
                      <div className="flex items-start gap-2">
                        <ScrollText className="h-4 w-4 mt-0.5 text-blue-500 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-1.5 text-xs">
                            <span
                              className={`font-mono px-1.5 py-0.5 rounded text-[11px] border ${
                                actionStyle
                                  ? `${actionStyle.bg} ${actionStyle.text} ${actionStyle.border}`
                                  : 'bg-muted text-foreground border-border'
                              }`}
                            >
                              {a.action}
                            </span>
                            <span className="text-muted-foreground">by</span>
                            <span className="font-medium">{a.username}</span>
                          </div>
                          <p className="mt-1 text-xs text-muted-foreground line-clamp-2">{a.details}</p>
                          <p className="mt-1 text-[11px] text-muted-foreground">{fmtTime(a.timestamp)}</p>
                        </div>
                      </div>
                    </div>
                  );
                })
              )
            )}
          </div>

          <div className="px-3 py-2 border-t border-border bg-muted/30">
            <p className="text-[10px] text-muted-foreground">
              Audit entries also written to <code className="text-foreground">/var/log/lokko-overseer/audit.log</code>
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function HeaderStrip({
  events, audit, isAdmin,
}: {
  events: FailoverEvent[];
  audit: AuditEntry[];
  isAdmin: boolean;
}) {
  if (!isAdmin) return null;
  return (
    <div className="flex items-center justify-end gap-1 sm:gap-2 px-3 sm:px-4 py-2">
      <FailoverHistoryButton events={events} audit={audit} />
    </div>
  );
}

// ---- Root ---------------------------------------------------------------

export function MultiOrgPreview() {
  const { user } = useAuth();
  // isAdmin via shared role-precedence helper so a user in both 'admin'
  // and 'monitor' (or any combination) still gets admin powers here.
  const isAdmin = getEffectiveRole(user?.groups) === 'admin';
  const username = user?.username || 'unknown@local';

  const [state, setState] = useState<FailoverStateResponse | null>(null);
  const [events, setEvents] = useState<FailoverEvent[]>([]);
  const [audit, setAudit] = useState<AuditEntry[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const [s, h, a] = await Promise.all([
        apiGet<FailoverStateResponse>('/api/v1/failover/state'),
        apiGet<{ events: FailoverEvent[] }>('/api/v1/failover/history'),
        apiGet<{ entries: AuditEntry[] }>('/api/v1/failover/audit'),
      ]);
      setState(s);
      setEvents(h.events || []);
      setAudit(a.entries || []);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 3000);
    return () => clearInterval(id);
  }, [refresh]);

  const togglePause = async () => {
    if (!isAdmin || !state || busy) return;
    const goingToPause = !state.paused;
    const confirmMsg = goingToPause
      ? [
          '⚠️  PAUSE auto-failover?',
          '',
          'While paused, the engine will:',
          '  • Continue probing health every 30s',
          '  • NOT trigger automatic failovers when services go critical',
          '  • NOT auto-switch-back when a preferred env recovers',
          '  • NOT modify remote-config or scale ASGs',
          '',
          'You will need to manually monitor + intervene if anything goes down.',
          '',
          'Continue?',
        ].join('\n')
      : [
          'RESUME auto-failover?',
          '',
          'The engine will:',
          '  • Resume automatic failover decisions on the next tick (~30s)',
          '  • Trigger failover after 3 consecutive critical cycles',
          '  • Auto-switch-back after 5 min of preferred-env health',
          '',
          'Continue?',
        ].join('\n');
    if (!window.confirm(confirmMsg)) return;

    setBusy(true);
    try {
      const path = goingToPause ? '/api/v1/failover/pause' : '/api/v1/failover/resume';
      await apiPost(path, { username });
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const force = async (org: string, env: string) => {
    if (!isAdmin || busy || !state) return;
    const isCrossOrg = state.active.org !== org;
    const targetLabel = `${org.toUpperCase()}/${env}`;
    const sourceLabel = `${state.active.org.toUpperCase()}/${state.active.env}`;
    const warning = isCrossOrg
      ? [
          `⚠️  CROSS-ORG PIN — this will shift REAL game traffic to ${targetLabel}.`,
          ``,
          `This will:`,
          `  • Scale ${targetLabel} ASG to 2 (real AWS call)`,
          `  • Rewrite Hostinger remote-config Host field`,
          `  • Send SNS notifications to all subscribers`,
          `  • Suspend auto-failover until you click "Release pin"`,
          ``,
          `If ${targetLabel} doesn't have working game servers, clients will get connection errors.`,
          ``,
          `Currently active: ${sourceLabel}`,
          `Pin to:           ${targetLabel}`,
          ``,
          `Continue?`,
        ].join('\n')
      : [
          `Pin ${targetLabel} as the active endpoint?`,
          ``,
          `This will:`,
          `  • Rewrite Hostinger remote-config Host field`,
          `  • Send SNS notifications to all subscribers`,
          `  • Suspend auto-failover until you click "Release pin"`,
          ``,
          `Currently active: ${sourceLabel}`,
          `Pin to:           ${targetLabel}`,
        ].join('\n');
    if (!window.confirm(warning)) return;

    setBusy(true);
    try {
      // Backend returns {success: bool, error?: string, forced?: ...}.
      // success=false happens when execute_switchover aborted (FTP rewrite
      // failed, etc.) -- pin was NOT applied. Surface the error so the
      // admin doesn't think the pin took effect when it didn't.
      const result = await apiPost<{ success: boolean; error?: string }>(
        '/api/v1/failover/force', { org, env, username }
      );
      if (!result.success) {
        setError(`Pin failed: ${result.error ?? 'switchover aborted (check backend logs)'}`);
      }
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const unforce = async () => {
    if (!isAdmin || busy) return;
    if (!window.confirm(
      'Release the manual pin?\n\nAuto-failover will resume on the next tick (~30s). ' +
      'Engine will switch back to the highest-priority healthy env if the current ' +
      'active env is below it in the priority chain.'
    )) return;
    setBusy(true);
    try {
      await apiPost('/api/v1/failover/unforce', { username });
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  if (!state) {
    return (
      <section className="space-y-3">
        {error
          ? <div className="p-3 rounded-lg bg-red-900/40 border border-red-700 text-red-300 text-sm">Failed to load failover state: {error}</div>
          : <div className="text-center text-muted-foreground text-sm py-4">Loading multi-org failover state…</div>
        }
      </section>
    );
  }

  if (!state.enabled || !state.active || !state.active.org) {
    // Engine disabled OR state corrupted (e.g. state file missing 'active').
    // Without this guard the org-panel render below dereferences state.active.org
    // and crashes the whole Monitor tab.
    return (
      <section>
        <div className="p-3 rounded-lg bg-yellow-900/30 border border-yellow-700 text-yellow-300 text-sm">
          Multi-org failover is not enabled. <code>config/failover.yaml</code> is missing or empty.
        </div>
      </section>
    );
  }

  // Build per-org panel data from the state
  const orgPanels = Object.entries(state.orgs).map(([orgId, orgInfo]) => {
    const envs: EnvViewData[] = (orgInfo.envs as string[]).map(envId => ({
      envId,
      services: state.fault_state[orgId]?.[envId] ?? {},
      asgDesired: state.asg_desired[orgId]?.[envId] ?? null,
      failStreak: state.consecutive_failures[orgId]?.[envId] ?? 0,
      healthyStreak: state.healthy_streak_seconds[orgId]?.[envId] ?? 0,
    }));
    const activeEnv = state.active.org === orgId ? state.active.env : null;
    return { orgId, orgInfo, envs, activeEnv };
  });

  return (
    <section className="space-y-4 sm:space-y-6">
      <CommonStatsRow />

      {error && (
        <div className="p-2 rounded bg-red-900/30 border border-red-700 text-red-300 text-xs">
          API error: {error}
        </div>
      )}

      <HeaderStrip events={events} audit={audit} isAdmin={isAdmin} />

      {isAdmin && (
        <FailoverControls
          paused={state.paused}
          forced={state.forced}
          onTogglePause={togglePause}
          onUnforce={unforce}
          busy={busy}
        />
      )}

      <div className="space-y-4 sm:space-y-6">
        {orgPanels.map(({ orgId, orgInfo, envs, activeEnv }) => (
          <OrgPanel
            key={orgId}
            orgId={orgId}
            orgDisplayName={`${orgInfo.display_name} (${orgInfo.aws_account_id})`}
            orgEnvs={envs}
            activeEnv={activeEnv}
            isAdmin={isAdmin}
            busy={busy}
            onForce={(env) => force(orgId, env)}
          />
        ))}
      </div>

      {isAdmin && state.priority_chain.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm sm:text-base flex items-center gap-2 flex-wrap">
              <Activity className="h-4 w-4" /> Failover priority chain
              <span className="text-[10px] sm:text-xs text-muted-foreground font-normal">
                (current Host: <code className="text-foreground">{state.remote_config_host}</code>)
              </span>
              <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
                <Shield className="h-3 w-3" /> Admin only
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 text-xs sm:text-sm">
              {state.priority_chain.map((step, i) => {
                const isActive = state.active.org === step.org && state.active.env === step.env;
                return (
                  <div key={`${step.org}-${step.env}`} className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                    {i > 0 && <ArrowRight className="h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground" />}
                    <span
                      className={`font-mono px-2 py-1 rounded border ${
                        isActive
                          ? 'border-green-500/40 bg-green-500/15 text-green-700 dark:text-green-400 font-semibold'
                          : 'border-border bg-muted/40'
                      }`}
                    >
                      {step.org.toUpperCase()} {step.env}
                    </span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </section>
  );
}
